/**
 * One shared, in-memory cache of iManage reads for the area. The sidebar and
 * the page sit in different trees, so they share it here: the same listing
 * is fetched once, Back and Forward render from cache at once, and a stale
 * entry (older than STALE_MS) is shown while it revalidates. Concurrent reads
 * of one key share one request. Document bodies are kept for only the last
 * few documents opened.
 *
 * Keys always include the organization, so nothing crosses organizations.
 *
 * @module components/apps/imanage/imanageQueries
 */
import * as Schema from "effect/Schema";
import {
  IManageDocument,
  IManageEntry,
  IManagePage,
  IManageStatus,
} from "@amoreg/contracts/imanage";
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { imanageRequest } from "../../../state/imanage";
import { resolveLibrary, useLibraryChoice } from "./imanageLocalLists";

export const STALE_MS = 30_000;
export const DOCUMENT_CACHE_LIMIT = 6;

export interface QueryCell {
  readonly data: unknown;
  readonly error: string | null;
  /** Epoch ms of the last success; 0 before any. */
  readonly at: number;
  readonly loading: boolean;
}

export interface QueryState<A> {
  readonly data: A | null;
  readonly error: string | null;
  /** True only while nothing is cached yet: show a skeleton. */
  readonly loading: boolean;
  /** True while a cached value is being refreshed. */
  readonly validating: boolean;
  readonly refresh: () => void;
}

/** Stable key: org, action, then params in name order. */
export function queryKey(
  organizationId: string,
  action: string,
  params: Readonly<Record<string, string>>,
): string {
  const query = Object.keys(params)
    .sort()
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key]!)}`)
    .join("&");
  return `${organizationId}|${action}|${query}`;
}

export const isStale = (cell: QueryCell | undefined, now: number, staleMs = STALE_MS) =>
  !cell || cell.at === 0 || now - cell.at > staleMs;

const cells = new Map<string, QueryCell>();
/** The running request per key, and the key's generation when it started. */
const inflight = new Map<string, { readonly promise: Promise<void>; readonly generation: number }>();
/** Bumped by every invalidation: a request started before it can no longer answer for the key. */
const generations = new Map<string, number>();
const generationOf = (key: string) => generations.get(key) ?? 0;
const listeners = new Map<string, Set<() => void>>();
const EMPTY_CELL: QueryCell = { data: null, error: null, at: 0, loading: false };

function set(key: string, cell: QueryCell) {
  cells.set(key, cell);
  for (const listener of listeners.get(key) ?? []) listener();
}

function evictDocuments() {
  const documents = [...cells.entries()].filter(([key]) => key.split("|")[1] === "document");
  const loaded = documents.filter(([, cell]) => cell.at > 0).sort((a, b) => a[1].at - b[1].at);
  while (loaded.length > DOCUMENT_CACHE_LIMIT) {
    const [key] = loaded.shift()!;
    if (!listeners.get(key)?.size) cells.delete(key);
  }
}

/**
 * Fetches a key unless a request for it is already running. A request that
 * started before the key was invalidated is not joined: it may have read the
 * server before a save, so a new one starts, and the older one's answer is
 * dropped rather than stored as fresh.
 */
export function fetchQuery(
  organizationId: string,
  action: string,
  params: Readonly<Record<string, string>>,
  decode: (raw: unknown) => unknown,
): Promise<void> {
  const key = queryKey(organizationId, action, params);
  const generation = generationOf(key);
  const running = inflight.get(key);
  if (running && running.generation === generation) return running.promise;
  const previous = cells.get(key) ?? EMPTY_CELL;
  set(key, { ...previous, loading: true });
  const isCurrent = () => inflight.get(key)?.promise === request && generationOf(key) === generation;
  // Superseded by an invalidation: store nothing; settle the spinner unless a newer request runs.
  const settleSuperseded = () => {
    if (inflight.get(key)?.promise === request) set(key, { ...(cells.get(key) ?? EMPTY_CELL), loading: false });
  };
  const request: Promise<void> = imanageRequest(organizationId, action, { ...params })
    .then((raw) => {
      let data: unknown;
      try {
        data = decode(raw);
      } catch {
        throw new Error("iManage returned an unexpected response.");
      }
      if (!isCurrent()) return settleSuperseded();
      set(key, { data, error: null, at: Date.now(), loading: false });
      if (action === "document") evictDocuments();
    })
    .catch((error: unknown) => {
      if (!isCurrent()) return settleSuperseded();
      set(key, {
        ...(cells.get(key) ?? EMPTY_CELL),
        error: error instanceof Error ? error.message : "iManage could not be reached.",
        loading: false,
      });
    })
    .finally(() => {
      if (inflight.get(key)?.promise === request) inflight.delete(key);
    });
  inflight.set(key, { promise: request, generation });
  return request;
}

/** Drops cached reads for an organization (all, or those whose action matches). */
export function invalidateIManage(organizationId: string, actions?: readonly string[]) {
  for (const key of [...cells.keys()]) {
    const [org, action] = key.split("|");
    if (org !== organizationId || (actions && !actions.includes(action!))) continue;
    const cell = cells.get(key)!;
    generations.set(key, generationOf(key) + 1);
    // Keep the data on screen; mark it stale so the next read refetches.
    set(key, { ...cell, at: cell.at > 0 ? 1 : 0 });
    if (listeners.get(key)?.size) {
      const [, , query] = key.split("|");
      const params = Object.fromEntries(new URLSearchParams(query));
      const decode = decoders.get(action!);
      if (decode) void fetchQuery(organizationId, action!, params, decode);
    }
  }
}

/** Forget everything (tests). Pages use invalidateIManage, which refetches what is on screen. */
export function resetIManageCache() {
  cells.clear();
  inflight.clear();
  generations.clear();
  listeners.clear();
}

const decoders = new Map<string, (raw: unknown) => unknown>();

export function useIManageQuery<A>(
  organizationId: string | null,
  action: string,
  params: Readonly<Record<string, string>> | null,
  decode: (raw: unknown) => A,
): QueryState<A> {
  decoders.set(action, decode);
  const key = organizationId && params ? queryKey(organizationId, action, params) : null;
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!key) return () => undefined;
      const bucket = listeners.get(key) ?? new Set<() => void>();
      listeners.set(key, bucket);
      bucket.add(listener);
      return () => {
        bucket.delete(listener);
      };
    },
    [key],
  );
  const cell = useSyncExternalStore(
    subscribe,
    () => (key ? (cells.get(key) ?? EMPTY_CELL) : EMPTY_CELL),
    () => EMPTY_CELL,
  );
  useEffect(() => {
    if (!key || !organizationId || !params) return;
    if (isStale(cells.get(key), Date.now()))
      void fetchQuery(organizationId, action, params, decode);
    // `key` captures action and params.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const refresh = useCallback(() => {
    if (key && organizationId && params) void fetchQuery(organizationId, action, params, decode);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return {
    data: cell.at > 0 ? (cell.data as A) : null,
    error: cell.error,
    loading: cell.at === 0 && (cell.loading || (key !== null && cell.error === null)),
    validating: cell.at > 0 && cell.loading,
    refresh,
  };
}

// ── Typed reads ────────────────────────────────────────────────────────────

const decodePage = Schema.decodeUnknownSync(IManagePage);
const decodeEntry = Schema.decodeUnknownSync(IManageEntry);
const decodeEntries = Schema.decodeUnknownSync(Schema.Array(IManageEntry));
const decodeDocument = Schema.decodeUnknownSync(IManageDocument);
const decodeStatus = Schema.decodeUnknownSync(IManageStatus);

export const useIManageStatusQuery = (organizationId: string | null) => {
  const state = useIManageQuery(organizationId, "status", {}, decodeStatus);
  const pending = state.data?.pending === true;
  const { refresh } = state;
  // While a sign-in is open in another tab, poll; and re-read on return to the tab.
  useEffect(() => {
    if (!organizationId) return;
    const timer = pending ? window.setInterval(refresh, 2000) : undefined;
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [organizationId, pending, refresh]);
  return state;
};

export const useLibraries = (organizationId: string | null, enabled: boolean) =>
  useIManageQuery(organizationId, "libraries", enabled ? {} : null, decodePage);

export const useWorkspaces = (organizationId: string | null, library: string | null) =>
  useIManageQuery(
    organizationId,
    "browse",
    library ? { libraryId: library, kind: "matter" } : null,
    decodePage,
  );

/** Children of a workspace (its folders) or of a folder (sub-folders, then documents). */
export const useChildren = (
  organizationId: string | null,
  library: string | null,
  parent: { readonly id: string; readonly kind: "matter" | "folder" } | null,
  offset = 0,
) =>
  useIManageQuery(
    organizationId,
    "browse",
    library && parent
      ? { libraryId: library, kind: parent.kind, parentId: parent.id, offset: String(offset) }
      : null,
    decodePage,
  );

export const useContainer = (
  organizationId: string | null,
  library: string | null,
  kind: "matter" | "folder",
  id: string | null,
) =>
  useIManageQuery(
    organizationId,
    "container",
    library && id ? { libraryId: library, kind, containerId: id } : null,
    decodeEntry,
  );

/** Library search, or a container's documents (empty `query` inside a container). */
export const useSearch = (
  organizationId: string | null,
  library: string | null,
  query: string,
  container: string | null,
  offset = 0,
) =>
  useIManageQuery(
    organizationId,
    "search",
    library && (query.trim() || container)
      ? {
          libraryId: library,
          query: query.trim(),
          ...(container ? { containerId: container } : {}),
          offset: String(offset),
        }
      : null,
    decodePage,
  );

export const useVersions = (
  organizationId: string | null,
  library: string | null,
  documentId: string | null,
) =>
  useIManageQuery(
    organizationId,
    "versions",
    library && documentId ? { libraryId: library, documentId } : null,
    decodeEntries,
  );

/** A document's profile and bytes; `exact` reads that version, else the latest. */
export const useDocumentContent = (
  organizationId: string | null,
  library: string | null,
  documentId: string | null,
  exact: boolean,
) =>
  useIManageQuery(
    organizationId,
    "document",
    library && documentId
      ? { libraryId: library, documentId, ...(exact ? { exact: "1" } : {}) }
      : null,
    decodeDocument,
  );

/**
 * The library a page shows: the one it names (`override`, e.g. a workspace
 * ID's prefix), else the person's last choice if still offered, else the
 * first library offered.
 */
export function useIManageLibrary(
  organizationId: string | null,
  connected: boolean,
  override: string | null = null,
) {
  const libraries = useLibraries(organizationId, connected);
  const [stored, choose] = useLibraryChoice(organizationId);
  const library = override ?? resolveLibrary(stored, libraries.data?.entries ?? null);
  return { library, libraries, choose };
}
